import { CSSProperties, useEffect } from "react";
import { useForm } from "@mantine/form";
import { shopVar } from "@/lib/shopStyles";
import { CustomerExtraInformation } from "@/lib/TicketApi";
import * as m from "@/paraglide/messages.js";

interface PaymentCustomerFieldsProps {
    // Wire keys from PaymentMethod.requiredCustomerFields: billingAddress | phone | birthDate
    requiredFields: string[];
    onChange: (customer: CustomerExtraInformation, isValid: boolean) => void;
    // Server-side backstop: missing/invalid fields from an extra_information_needed response.
    serverMissingFields?: string[];
}

const inputClassName = "block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 placeholder:text-[color:var(--shop-textColor)] placeholder:opacity-60";

const inputStyle = {
    color: shopVar('--shop-textColor'),
    backgroundColor: shopVar('--shop-panelBackgroundColor'),
    '--tw-ring-color': shopVar('--shop-borderColor'),
} as CSSProperties;

const labelStyle = { color: shopVar('--shop-textColor') };

export function PaymentCustomerFields({ requiredFields, onChange, serverMissingFields }: PaymentCustomerFieldsProps) {
    const needsAddress = requiredFields.includes('billingAddress');
    const needsPhone = requiredFields.includes('phone');
    const needsBirthDate = requiredFields.includes('birthDate');

    const form = useForm({
        mode: 'controlled',
        initialValues: {
            phone: '',
            birthDate: '',
            streetName: '',
            streetNumber: '',
            zipCode: '',
            city: '',
            countryCode: 'NL',
        },
        validateInputOnChange: true,
        validate: {
            phone: (value: string) => (!needsPhone || value.trim().length > 0 ? null : m.field_required()),
            birthDate: (value: string) => (!needsBirthDate || /^\d{4}-\d{2}-\d{2}$/.test(value) ? null : m.field_required()),
            streetName: (value: string) => (!needsAddress || value.trim().length > 0 ? null : m.field_required()),
            streetNumber: (value: string) => (!needsAddress || value.trim().length > 0 ? null : m.field_required()),
            zipCode: (value: string) => (!needsAddress || value.trim().length > 0 ? null : m.field_required()),
            city: (value: string) => (!needsAddress || value.trim().length > 0 ? null : m.field_required()),
            countryCode: (value: string) => (!needsAddress || /^[A-Za-z]{2}$/.test(value.trim()) ? null : m.field_required()),
        },
        onValuesChange: (values) => {
            onChange(toCustomer(values), form.isValid());
        },
    });

    const toCustomer = (values: typeof form.values): CustomerExtraInformation => ({
        ...(needsPhone ? { phone: values.phone.trim() } : {}),
        ...(needsBirthDate ? { birthDate: values.birthDate } : {}),
        ...(needsAddress ? {
            billingAddress: {
                streetName: values.streetName.trim(),
                streetNumber: values.streetNumber.trim(),
                zipCode: values.zipCode.trim(),
                city: values.city.trim(),
                countryCode: values.countryCode.trim().toUpperCase(),
            },
        } : {}),
    });

    // Re-report validity when the selected method (and thus the required fields) changes.
    useEffect(() => {
        onChange(toCustomer(form.values), form.isValid());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requiredFields.join(',')]);

    const fieldError = (field: string, serverKey: string) => {
        if (form.errors[field]) {
            return <p className="mt-2 text-sm text-red-600">{form.errors[field]}</p>;
        }
        if (serverMissingFields?.includes(serverKey)) {
            return <p className="mt-2 text-sm text-red-600">{m.field_required()}</p>;
        }
        return null;
    };

    return (
        <div className="grid grid-cols-1 gap-4">
            <span className="text-sm" style={labelStyle}>{m.payment_method_extra_details()}</span>

            {(needsPhone || needsBirthDate) && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-4">
                    {needsPhone && (
                        <div className="col-span-1">
                            <label htmlFor="payment_phone" className="block text-sm font-medium leading-6" style={labelStyle}>
                                {m.form_phone()}
                            </label>
                            <div className="mt-2">
                                <input
                                    id="payment_phone"
                                    type="tel"
                                    autoComplete="tel"
                                    placeholder="0612345678"
                                    className={inputClassName}
                                    {...form.getInputProps('phone')}
                                    style={inputStyle}
                                />
                                {fieldError('phone', 'phone')}
                            </div>
                        </div>
                    )}
                    {needsBirthDate && (
                        <div className="col-span-1">
                            <label htmlFor="payment_birth_date" className="block text-sm font-medium leading-6" style={labelStyle}>
                                {m.form_birth_date()}
                            </label>
                            <div className="mt-2">
                                <input
                                    id="payment_birth_date"
                                    type="date"
                                    autoComplete="bday"
                                    className={inputClassName}
                                    {...form.getInputProps('birthDate')}
                                    style={inputStyle}
                                />
                                {fieldError('birthDate', 'birthDate')}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {needsAddress && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-6 sm:gap-4">
                    <div className="sm:col-span-4">
                        <label htmlFor="payment_street" className="block text-sm font-medium leading-6" style={labelStyle}>
                            {m.form_street_name()}
                        </label>
                        <div className="mt-2">
                            <input
                                id="payment_street"
                                type="text"
                                autoComplete="address-line1"
                                className={inputClassName}
                                {...form.getInputProps('streetName')}
                                style={inputStyle}
                            />
                            {fieldError('streetName', 'billingAddress')}
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="payment_street_number" className="block text-sm font-medium leading-6" style={labelStyle}>
                            {m.form_street_number()}
                        </label>
                        <div className="mt-2">
                            <input
                                id="payment_street_number"
                                type="text"
                                className={inputClassName}
                                {...form.getInputProps('streetNumber')}
                                style={inputStyle}
                            />
                            {fieldError('streetNumber', 'billingAddress')}
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="payment_zip" className="block text-sm font-medium leading-6" style={labelStyle}>
                            {m.form_zip_code()}
                        </label>
                        <div className="mt-2">
                            <input
                                id="payment_zip"
                                type="text"
                                autoComplete="postal-code"
                                placeholder="1234 AB"
                                className={inputClassName}
                                {...form.getInputProps('zipCode')}
                                style={inputStyle}
                            />
                            {fieldError('zipCode', 'billingAddress')}
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="payment_city" className="block text-sm font-medium leading-6" style={labelStyle}>
                            {m.form_city()}
                        </label>
                        <div className="mt-2">
                            <input
                                id="payment_city"
                                type="text"
                                autoComplete="address-level2"
                                className={inputClassName}
                                {...form.getInputProps('city')}
                                style={inputStyle}
                            />
                            {fieldError('city', 'billingAddress')}
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="payment_country" className="block text-sm font-medium leading-6" style={labelStyle}>
                            {m.form_country()}
                        </label>
                        <div className="mt-2">
                            <select
                                id="payment_country"
                                autoComplete="country"
                                className={inputClassName}
                                {...form.getInputProps('countryCode')}
                                style={inputStyle}
                            >
                                <option value="NL">Nederland</option>
                                <option value="BE">België</option>
                                <option value="DE">Deutschland</option>
                                <option value="FR">France</option>
                                <option value="GB">United Kingdom</option>
                            </select>
                            {fieldError('countryCode', 'billingAddress')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
